import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowLeft } from 'lucide-react';
import { Assert } from '@cotera/utilities';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Text } from '@cotera/client/app/components/ui';
import { PersistedWorkflow } from '@cotera/sdk/core';
import {
  resolveTriggerRenderableNode,
  useWorkflowRenderCatalog,
} from '../workflow-render-catalog';
import {
  TriggerPickerContent,
  type TriggerSelection,
} from './trigger-picker-content';
import { WorkflowEditPanel } from './workflow-edit-panel';
import type { WorkflowEditLayout } from './workflow-edit-layout';
import { SchedulePicker } from '../../trigger/schedule-picker';
import { humanReadableSchedule } from '../../trigger/schedule-utils';
import { useWorkflowTriggerOptions } from '../../trigger/hooks';
import { useEmitEventNameSuggestions } from '../hooks/use-emit-event-name-suggestions';
import { useWorkflowGraphMutations } from '../use-workflow-graph-mutations';
import { useWorkflowDrill } from '../workflow-drill-context';
import { useWorkflowModel } from '../workflow.model';
import { isDraftOnlyTriggerInWorkflow } from '../workflow-draft-graph';
import type { WorkflowFlowTriggerData } from './workflow-flow-trigger';
import {
  CronTriggerEditor,
  DatasetTriggerEditor,
  EventTriggerEditor,
  WebhookTriggerEditor,
} from './trigger/edit-strategies';
import {
  WebhookProviderConfigSection,
  WebhookProviderPicker,
  createDefaultWebhookProviderConfigDraft,
  findWebhookProviderCatalogEntry,
  parseWebhookProviderKey,
  useWebhookProviderCatalog,
  useWebhookProviderConfigDraft,
} from '@cotera/client/app/v1/webhook-providers';
import type {
  WorkflowCronTriggerViewData,
  WorkflowDatasetTriggerViewData,
  WorkflowEventTriggerViewData,
  WorkflowWebhookTriggerViewData,
} from './trigger/types';
import { WorkflowIdempotencyKeyEditor } from './workflow-idempotency-key-editor';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

// A step inside the add-trigger navigator. `picker` is the root (existing
// triggers + "create new"); the others are drill-in configuration steps that
// slide over it, mirroring the add-node picker.
type TriggerStep =
  | 'picker'
  | 'cron'
  | 'event'
  | 'webhook-provider'
  | 'webhook-config';

// One calm, directional slide+fade between steps — `dir` is +1 drilling in,
// -1 stepping back. Matches AddWorkflowNodePicker so the two flows feel alike.
const STEP_TRANSITION = { duration: 0.2, ease: [0.4, 0, 0.2, 1] } as const;

const STEP_VARIANTS = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 16 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -16 }),
};

/** Back affordance + title for a drilled-in configuration step. */
const StepHeader: React.FC<{
  title: string;
  subtitle?: string;
  onBack: () => void;
}> = ({ title, subtitle, onBack }) => (
  <div className="mb-4">
    <button
      type="button"
      onClick={onBack}
      className="mb-3 -ml-1 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back
    </button>
    <Text.P className="font-medium leading-tight">{title}</Text.P>
    {subtitle !== undefined && subtitle !== '' ? (
      <Text.Caption className="text-muted-foreground mt-0.5 block">
        {subtitle}
      </Text.Caption>
    ) : null}
  </div>
);

function isWebhookTriggerView(
  trigger: WorkflowFlowTriggerData['trigger'] | null
): trigger is WorkflowWebhookTriggerViewData {
  return trigger?.kind === PersistedWorkflowTriggerKind.Webhook;
}

function isCronTriggerView(
  trigger: WorkflowFlowTriggerData['trigger'] | null
): trigger is WorkflowCronTriggerViewData {
  return trigger?.kind === PersistedWorkflowTriggerKind.Cron;
}

function isEventTriggerView(
  trigger: WorkflowFlowTriggerData['trigger'] | null
): trigger is WorkflowEventTriggerViewData {
  return trigger?.kind === PersistedWorkflowTriggerKind.Event;
}

function isDatasetTriggerView(
  trigger: WorkflowFlowTriggerData['trigger'] | null
): trigger is WorkflowDatasetTriggerViewData {
  return trigger?.kind === PersistedWorkflowTriggerKind.Dataset;
}

/**
 * Floating panel for configuring a workflow's current trigger — the same
 * in-canvas panel as the node config. Cron and webhook nodes open their own
 * in-place editors (schedule picker / webhook settings). Other cases still use
 * the existing-triggers picker to swap bindings. Adding a brand-new trigger is
 * opened via {@link WorkflowToolbarAddTriggerAction}, not this panel.
 */
export const WorkflowTriggerEditDrawer: React.FC<{
  open: boolean;
  current: WorkflowFlowTriggerData;
  layout?: WorkflowEditLayout;
}> = ({ open, current, layout = 'panel' }) => {
  const model = useWorkflowModel();
  const mutations = useWorkflowGraphMutations();
  // Cancel and a successful save both return to the workflow canvas artifact.
  const { close } = useWorkflowDrill();
  const workflowId = model.workflowId;
  const existingTriggers = useWorkflowTriggerOptions();
  const eventNameSuggestions = useEmitEventNameSuggestions();
  const { providers, isLoading: catalogLoading } = useWebhookProviderCatalog();
  const [selection, setSelection] = useState<TriggerSelection | null>(null);
  const [cronDraft, setCronDraft] = useState('0 9 * * *');
  const [nameDraft, setNameDraft] = useState('');
  const [eventNameDraft, setEventNameDraft] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [webhookCreateProviderKey, setWebhookCreateProviderKey] = useState<
    string | null
  >(null);
  const {
    draft: webhookDraft,
    patchDraft: patchWebhookDraft,
    resetDraft,
  } = useWebhookProviderConfigDraft();
  const [createdWebhook, setCreatedWebhook] = useState<{
    triggerId: string;
    secret: string;
  } | null>(null);
  const [step, setStep] = useState<TriggerStep>('picker');
  // +1 drills deeper, -1 steps back — drives the slide direction.
  const [stepDirection, setStepDirection] = useState(1);
  const eventNameListId = `workflow-trigger-event-names-${current.trigger.id}`;

  const navigate = (next: TriggerStep, dir: number): void => {
    setStepDirection(dir);
    setStep(next);
  };

  const currentView = current.trigger;
  const currentTriggerId = currentView.id;
  const bindingId = current.bindingId;
  const isDraftTrigger = isDraftOnlyTriggerInWorkflow(
    model.getDraftState(),
    currentTriggerId,
    model.isNew
  );
  const editingCurrentWebhook =
    isWebhookTriggerView(currentView) &&
    selection?.type === 'existing' &&
    selection.triggerId === currentTriggerId &&
    bindingId !== undefined &&
    !isDraftTrigger;

  // Cron nodes always open the schedule picker (same as create), not the
  // existing-triggers swap list — mirrors webhook's in-place editor.
  const editingCurrentCron =
    isCronTriggerView(currentView) &&
    bindingId !== undefined &&
    (isDraftTrigger ||
      (selection?.type === 'existing' &&
        selection.triggerId === currentTriggerId));

  const editingCurrentEvent =
    isEventTriggerView(currentView) &&
    bindingId !== undefined &&
    (isDraftTrigger ||
      (selection?.type === 'existing' &&
        selection.triggerId === currentTriggerId));

  const createCatalogEntry = useMemo(
    () =>
      webhookCreateProviderKey === null
        ? undefined
        : findWebhookProviderCatalogEntry(providers, webhookCreateProviderKey),
    [providers, webhookCreateProviderKey]
  );

  useEffect(() => {
    if (open) {
      // Reopening always resets the navigator to a well-defined starting step
      // (no residual slide), then jumps straight to the config step when we're
      // reopening a draft trigger the user is mid-editing.
      setStepDirection(1);
      if (isDraftTrigger) {
        if (isWebhookTriggerView(currentView)) {
          setSelection({ type: 'new-webhook' });
          setWebhookCreateProviderKey(currentView.providerKey ?? 'generic');
          resetDraft({
            name: currentView.name,
            providerKey: parseWebhookProviderKey(currentView.providerKey),
            eventKey: currentView.eventKey ?? null,
            eventConfig: toRecord(currentView.eventConfig),
            verificationConfig: undefined,
            enabled: currentView.enabled,
          });
          setCreatedWebhook({
            triggerId: currentTriggerId,
            secret: 'save-workflow-to-reveal-secret',
          });
          setStep('webhook-config');
        } else if (currentView.kind === PersistedWorkflowTriggerKind.Cron) {
          setSelection({ type: 'new-cron' });
          setCronDraft(currentView.cron);
          setNameDraft(currentView.name);
          setNameEdited(true);
          setCreatedWebhook(null);
          setStep('cron');
        } else if (currentView.kind === PersistedWorkflowTriggerKind.Event) {
          setSelection({ type: 'new-event' });
          setNameDraft(currentView.name);
          setEventNameDraft(currentView.eventName);
          setCreatedWebhook(null);
          setStep('event');
        } else {
          setSelection(null);
          setCreatedWebhook(null);
          setStep('picker');
        }
      } else {
        setSelection({ type: 'existing', triggerId: currentTriggerId });
        setCreatedWebhook(null);
        setStep('picker');
      }
    }
  }, [open, currentTriggerId, currentView, isDraftTrigger, resetDraft]);

  useEffect(() => {
    if (!nameEdited) {
      setNameDraft(humanReadableSchedule(cronDraft));
    }
  }, [cronDraft, nameEdited]);

  const catalog = useWorkflowRenderCatalog();
  const strategy = resolveTriggerRenderableNode(catalog, currentView).view;
  const headerTitle = strategy.getTitle(currentView);
  const headerSubtitle = strategy.getDescription?.(currentView);
  const headerIcon = strategy.renderIcon?.(currentView);

  const draftGraph = model.getDraftState().graph;
  const hasCronOrWebhookTrigger = draftGraph.triggerBindings.some((binding) => {
    if (!binding.enabled) {
      return false;
    }
    const snapshot = draftGraph.triggers.find(
      (row) => row.trigger.id === binding.triggerId
    );
    return (
      snapshot?.kind === PersistedWorkflowTriggerKind.Cron ||
      snapshot?.kind === PersistedWorkflowTriggerKind.Webhook ||
      snapshot?.kind === PersistedWorkflowTriggerKind.Event
    );
  });
  const showIdempotencyEditor =
    hasCronOrWebhookTrigger ||
    selection?.type === 'new-cron' ||
    selection?.type === 'new-webhook' ||
    selection?.type === 'new-event' ||
    (selection?.type === 'existing' &&
      draftGraph.triggers.some(
        (row) =>
          row.trigger.id === selection.triggerId &&
          (row.kind === PersistedWorkflowTriggerKind.Cron ||
            row.kind === PersistedWorkflowTriggerKind.Webhook ||
            row.kind === PersistedWorkflowTriggerKind.Event)
      ));

  const idempotencyEditor = showIdempotencyEditor ? (
    <div className="mt-4">
      <WorkflowIdempotencyKeyEditor
        value={model.getIdempotencyKey()}
        onChange={(next) => model.setIdempotencyKey(next)}
        disabled={saving}
      />
    </div>
  ) : null;

  const handleSave = async (): Promise<void> => {
    if (selection === null) {
      return;
    }
    if (
      selection.type === 'existing' &&
      selection.triggerId === currentTriggerId &&
      editingCurrentWebhook
    ) {
      close();
      return;
    }
    if (
      selection.type === 'existing' &&
      selection.triggerId === currentTriggerId &&
      !isDraftTrigger
    ) {
      close();
      return;
    }
    setSaving(true);
    let attached = false;
    if (selection.type === 'existing') {
      attached = await mutations.bindExistingTrigger(selection.triggerId);
    } else if (selection.type === 'new-cron') {
      attached = await mutations.addCronTrigger(
        cronDraft,
        undefined,
        nameDraft
      );
    } else if (selection.type === 'new-event') {
      const result = await mutations.addEventTrigger(nameDraft, eventNameDraft);
      if (result.success) {
        attached = true;
      }
    } else {
      if (createCatalogEntry === undefined) {
        setSaving(false);
        return;
      }
      const result = await mutations.addWebhookTrigger({
        name: webhookDraft.name,
        providerKey: webhookDraft.providerKey,
        verificationConfig: webhookDraft.verificationConfig,
        eventKey: webhookDraft.eventKey ?? undefined,
        eventConfig: webhookDraft.eventConfig,
        endpointSecret: webhookDraft.endpointSecret.trim() || undefined,
      });
      if (result.success) {
        attached = true;
        setCreatedWebhook({
          triggerId: result.triggerId,
          secret: result.secret,
        });
      }
    }
    if (!attached) {
      setSaving(false);
      return;
    }
    if (bindingId !== undefined) {
      const detached = await mutations.archiveTriggerBinding(bindingId);
      if (!detached) {
        setSaving(false);
        return;
      }
    }
    setSaving(false);
    close();
  };

  const showWebhookCreateForm = selection?.type === 'new-webhook';
  const webhookCreateReady =
    createCatalogEntry !== undefined &&
    (createCatalogEntry.events.length === 0 ||
      webhookDraft.eventKey !== null ||
      createCatalogEntry.setup.kind === 'none');

  // The navigator's current step, rendered inside a single sliding surface.
  // "New cron"/"New webhook" drill into their own step instead of stacking
  // their forms under the picker, so each screen stays focused.
  const stepBody = ((): React.ReactNode => {
    switch (step) {
      case 'picker':
        return (
          <TriggerPickerContent
            existingTriggers={existingTriggers}
            selection={selection}
            onSelectionChange={(next) => {
              setSelection(next);
              if (next.type === 'new-cron') {
                navigate('cron', 1);
              } else if (next.type === 'new-event') {
                setNameDraft('');
                setEventNameDraft('');
                navigate('event', 1);
              } else if (next.type === 'new-webhook') {
                setWebhookCreateProviderKey(null);
                resetDraft(createDefaultWebhookProviderConfigDraft());
                setCreatedWebhook(null);
                navigate('webhook-provider', 1);
              }
            }}
          />
        );
      case 'cron':
        return (
          <div>
            <StepHeader
              title="New schedule"
              subtitle="Give it a name and pick when it repeats"
              onBack={() => {
                setSelection(null);
                navigate('picker', -1);
              }}
            />
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Text.Caption className="text-muted-foreground block">
                  Name
                </Text.Caption>
                <Input
                  value={nameDraft}
                  onChange={(e) => {
                    setNameEdited(true);
                    setNameDraft(e.target.value);
                  }}
                  placeholder="Name this schedule"
                />
              </div>
              <div className="space-y-1.5">
                <Text.Caption className="text-muted-foreground block">
                  Schedule
                </Text.Caption>
                <SchedulePicker value={cronDraft} onChange={setCronDraft} />
              </div>
            </div>
          </div>
        );
      case 'event':
        return (
          <div>
            <StepHeader
              title="New event trigger"
              subtitle="Listen for a named event from another workflow"
              onBack={() => {
                setSelection(null);
                navigate('picker', -1);
              }}
            />
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Text.Caption className="text-muted-foreground block">
                  Name
                </Text.Caption>
                <Input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  placeholder="Name this event trigger"
                />
              </div>
              <div className="space-y-1.5">
                <Text.Caption className="text-muted-foreground block">
                  Event name
                </Text.Caption>
                <Input
                  value={eventNameDraft}
                  list={
                    eventNameSuggestions.length > 0
                      ? eventNameListId
                      : undefined
                  }
                  onChange={(e) => setEventNameDraft(e.target.value)}
                  placeholder="order.created"
                />
                {eventNameSuggestions.length > 0 ? (
                  <datalist id={eventNameListId}>
                    {eventNameSuggestions.map((suggestion) => (
                      <option key={suggestion} value={suggestion} />
                    ))}
                  </datalist>
                ) : null}
              </div>
            </div>
          </div>
        );
      case 'webhook-provider':
        return (
          <div>
            <StepHeader
              title="New webhook"
              subtitle="Choose a provider to receive inbound events from"
              onBack={() => {
                setSelection(null);
                setWebhookCreateProviderKey(null);
                navigate('picker', -1);
              }}
            />
            {catalogLoading ? (
              <Text.Caption className="text-muted-foreground block">
                Loading webhook providers…
              </Text.Caption>
            ) : (
              <WebhookProviderPicker
                providers={providers}
                selectedKey={webhookCreateProviderKey}
                onSelect={(providerKey) => {
                  setWebhookCreateProviderKey(providerKey);
                  resetDraft(
                    createDefaultWebhookProviderConfigDraft(providerKey)
                  );
                  navigate('webhook-config', 1);
                }}
              />
            )}
          </div>
        );
      case 'webhook-config':
        return (
          <div>
            <StepHeader
              title={createCatalogEntry?.displayName ?? 'Configure webhook'}
              subtitle="Name it and configure the inbound event"
              onBack={() => {
                setWebhookCreateProviderKey(null);
                resetDraft(createDefaultWebhookProviderConfigDraft());
                setCreatedWebhook(null);
                navigate('webhook-provider', -1);
              }}
            />
            {createCatalogEntry !== undefined ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Text.Caption className="text-muted-foreground block">
                    Name
                  </Text.Caption>
                  <Input
                    value={webhookDraft.name}
                    onChange={(event) =>
                      patchWebhookDraft({ name: event.target.value })
                    }
                  />
                </div>
                <WebhookProviderConfigSection
                  mode="create"
                  catalog={createCatalogEntry}
                  draft={webhookDraft}
                  onDraftChange={patchWebhookDraft}
                  triggerId={createdWebhook?.triggerId}
                  secret={createdWebhook?.secret}
                  onSave={async () => {}}
                  saving={saving}
                />
              </div>
            ) : null}
          </div>
        );
      default:
        return Assert.unreachable(step);
    }
  })();

  const editorBody =
    editingCurrentWebhook &&
    isWebhookTriggerView(currentView) &&
    bindingId !== undefined ? (
      <WebhookTriggerEditor
        workflowId={workflowId}
        bindingId={bindingId}
        trigger={currentView}
        onSaved={close}
      />
    ) : editingCurrentCron &&
      isCronTriggerView(currentView) &&
      bindingId !== undefined ? (
      <CronTriggerEditor
        workflowId={workflowId}
        bindingId={bindingId}
        trigger={currentView}
        onSaved={close}
      />
    ) : editingCurrentEvent &&
      isEventTriggerView(currentView) &&
      bindingId !== undefined ? (
      <EventTriggerEditor
        workflowId={workflowId}
        bindingId={bindingId}
        trigger={currentView}
        onSaved={close}
      />
    ) : isDatasetTriggerView(currentView) ? (
      <DatasetTriggerEditor
        workflowId={workflowId}
        trigger={currentView}
        onOpened={close}
      />
    ) : (
      <>
        <AnimatePresence
          mode="popLayout"
          custom={stepDirection}
          initial={false}
        >
          <motion.div
            key={step}
            custom={stepDirection}
            variants={STEP_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={STEP_TRANSITION}
          >
            {stepBody}
          </motion.div>
        </AnimatePresence>
        {idempotencyEditor}
      </>
    );

  const footer =
    editingCurrentWebhook ||
    editingCurrentCron ||
    editingCurrentEvent ||
    isDatasetTriggerView(currentView) ? null : (
      <>
        <Button variant="outline" onClick={close}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleSave()}
          disabled={
            selection === null ||
            saving ||
            (selection.type === 'new-cron' && cronDraft.trim() === '') ||
            (selection.type === 'new-event' &&
              (nameDraft.trim() === '' || eventNameDraft.trim() === '')) ||
            (showWebhookCreateForm &&
              (catalogLoading ||
                webhookCreateProviderKey === null ||
                !webhookCreateReady))
          }
        >
          Save changes
        </Button>
      </>
    );

  if (!open) {
    return null;
  }

  if (layout === 'page') {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
        {/* overflow-x-hidden: `overflow-y-auto` alone forces overflow-x from
            `visible` to `auto`, spawning a spurious horizontal scrollbar. */}
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl px-5 py-6">{editorBody}</div>
        </div>
        {footer !== null ? (
          <div className="shrink-0 border-t border-border/50 bg-background px-5 py-3">
            <div className="mx-auto flex w-full max-w-5xl justify-end gap-2">
              {footer}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <WorkflowEditPanel
      open={open}
      // See the node drawer: only the floating inspector lacks its own dismiss.
      {...(layout === 'inspector' ? { onClose: close } : {})}
      icon={headerIcon}
      accent={strategy.accent}
      badge={strategy.badge}
      title={headerTitle}
      subtitle={headerSubtitle}
      footer={footer}
    >
      {editorBody}
    </WorkflowEditPanel>
  );
};

function toRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}
